import { Epg, GridItemProps, Layout, Line as LineProps, useEpg } from '@interaction-gaming/planby'
import { parseISO } from 'date-fns'
import { useState, useMemo, useEffect } from 'react'
import { theme } from './theme'
import {
  ClaimCenterBookableAndSchedule,
  ClaimReservationWithTimeslot,
  ScheduleException,
} from '@Generated/back-office-client/src'
import { Bookable, GridDisplayMode, ReservationGridDisplayItem } from './types'
import { ReservationsData } from '@Components/claims/claims.tsx'
import {
  CustomChannelItem,
  CustomTimeline,
  GridCell,
  ReservationGridItem,
} from './schedule-components'
import { isEmpty } from 'lodash'
import { convertDateToIsoDate, formatDateToDayString, getTodayAsUTC } from '@Components/util/utils'
import {
  getFillerData,
  mapExceptionToDisplay,
  mapReservationToDisplay,
} from './reservation-grid-utils'

export const ReservationGrid = ({
  dates,
  startHour,
  endHour,
  onExceptionSelected,
  onReservationSelected,
  onClaimFormClick,
  onGridCellClick,
  reservationsData,
  claimCenterBookablesByDates,
  gridDataLoading,
  gridDisplayMode,
}: {
  dates: string[]
  startHour: number
  endHour: number
  onReservationSelected: (reservation: ClaimReservationWithTimeslot) => void
  onExceptionSelected: (exception: ScheduleException) => void
  onClaimFormClick: (reservationId: string) => void
  onGridCellClick: (gridCellProps: GridItemProps) => void
  reservationsData?: ReservationsData
  claimCenterBookablesByDates: Record<string, ClaimCenterBookableAndSchedule[]>
  gridDataLoading: boolean
  gridDisplayMode: GridDisplayMode
}) => {
  const [columns, setColumns] = useState<Bookable[]>([])
  const [reservations, setReservations] = useState<ReservationGridDisplayItem[] | null>(null)
  const [ready, setReady] = useState<boolean>(false)

  const isWeekMode = gridDisplayMode === GridDisplayMode.WEEK
  const today = convertDateToIsoDate(getTodayAsUTC())

  /**
   * Planby calendar mode (when displaying more than one day how we want to) wants all the times to be for the same day.
   * And the "channel" becomes what groups things into dates
   * This essentially acts the same as the day view but groups things differently - rather than by booking its by date.
   *
   * Current day isn't actually the calendar day - its the day we're using to group things with.
   *
   * If we're on the week view, and today is visible, we need to treat today as the render day so that the time line shows up.
   */

  const currentDay = isWeekMode && dates.includes(today) ? today : dates[0]

  useEffect(() => {
    if (isEmpty(claimCenterBookablesByDates)) {
      return
    }

    const columnGrouping =
      gridDisplayMode === GridDisplayMode.DAY
        ? Object.values(claimCenterBookablesByDates)[0].filter((b) => b.schedules.length > 0)
        : dates
    const columnsForRender: Bookable[] = columnGrouping.map((grouping) => ({
      uuid: typeof grouping === 'string' ? grouping : grouping.bookable.id,
      formattedDay: typeof grouping === 'string' ? formatDateToDayString(parseISO(grouping)) : null,
      gridDisplayMode,
      isToday: typeof grouping === 'string' ? grouping === today : true,
      logo: '',
    })) as Bookable[]

    let reservations: ReservationGridDisplayItem[] =
      reservationsData?.reservations.map((r) => {
        return mapReservationToDisplay(r, gridDisplayMode, currentDay)
      }) || []

    // We have an open issue with planby about this. Without this, this sometimes results in items in the WEEK view improperly overlapping.
    // This appears fixed if sorting the items first.
    // https://gambyt.slack.com/archives/C07UR3JM51V/p1755109940776099
    reservations = [...reservations].sort((a, b) => {
      return a.since > b.since ? 1 : a.since < b.since ? -1 : 0
    })

    const claimCenterExceptions: ReservationGridDisplayItem[] =
      reservationsData?.exceptions.flatMap((exception) => {
        // Lots of nesting here, but for the week view, since we're grouping things by date, we need to make sure
        // we go through each bookable and add a duplicate exception to the day
        return exception.claimCenterExceptions.flatMap((claimCenterException) => {
          const bookablesForDay = claimCenterBookablesByDates[
            claimCenterException.localDate
          ].filter((b) => b.schedules.length > 0)
          return bookablesForDay.flatMap((b) => {
            return mapExceptionToDisplay(
              claimCenterException,
              gridDisplayMode,
              gridDisplayMode === GridDisplayMode.DAY
                ? b.bookable.id
                : claimCenterException.localDate,
              b.bookable.id,
              currentDay,
              startHour,
              endHour,
            )
          })
        })
      }) || []

    const bookableExceptions: ReservationGridDisplayItem[] =
      reservationsData?.exceptions.flatMap((exception) => {
        return exception.bookableExceptions.map((bookableException) => {
          return mapExceptionToDisplay(
            bookableException,
            gridDisplayMode,
            gridDisplayMode === GridDisplayMode.DAY
              ? bookableException.claimCenterBookableId
              : bookableException.localDate,
            bookableException.claimCenterBookableId,
            currentDay,
            startHour,
            endHour,
          )
        })
      }) || []

    setColumns(columnsForRender)
    // Note: We intentionally put the reservations last so they are on top of exceptions if things overlap
    const gridItems = [...claimCenterExceptions, ...bookableExceptions, ...reservations]
    // If we're in grid mode, we want the grid to appear such that each day has the width of two bookables
    const fillerData = getFillerData(dates, gridItems, gridDisplayMode, currentDay)
    setReservations([...gridItems, ...fillerData])
    setReady(!gridDataLoading)
  }, [
    claimCenterBookablesByDates,
    currentDay,
    dates,
    endHour,
    gridDataLoading,
    gridDisplayMode,
    reservationsData,
    startHour,
    today,
  ])

  const { start, end } = useMemo(() => {
    return {
      start: `${currentDay}T${String(startHour).padStart(2, '0')}:00:00`,
      end: `${currentDay}T${String(endHour + 1).padStart(2, '0')}:59:59`,
    }
  }, [currentDay, endHour, startHour])

  const columnsForDisplay = useMemo(() => columns, [columns])
  const reservationsForDisplay = useMemo(() => reservations, [reservations])
  const hoursInDay = endHour - startHour

  const headerHeight = isWeekMode ? 50 : 0
  const gridHeight = hoursInDay * (isWeekMode ? 300 : 600) // Controls the size of the reservation cell that renders

  useEffect(() => {
    // Properly applies the background styling to the whole container
    const el = document.getElementById('planby-layout-scrollbox')

    if (isWeekMode) {
      el?.style.setProperty('--scrollbox-alpha', `.1`)
      el?.style.setProperty('--scrollbox-top-padding', `0`)
    } else {
      el?.style.removeProperty('--scrollbox-alpha')
      el?.style.removeProperty('--scrollbox-top-padding')
    }
  }, [gridHeight, headerHeight, isWeekMode])

  const { getEpgProps, getLayoutProps } = useEpg({
    channels: columnsForDisplay,
    epg: reservationsForDisplay || [],
    dayWidth: gridHeight,
    itemHeight: isWeekMode ? 120 : 210,
    isSidebar: isWeekMode,
    sidebarWidth: headerHeight,
    isTimeline: true,
    isLine: true,
    isVerticalMode: true,
    isBaseTimeFormat: true,
    timelineHeight: 100,
    grid: {
      enabled: true,
      onGridItemClick: isWeekMode ? onGridCellClick : () => {},
    },
    overlap: {
      enabled: isWeekMode,
      mode: 'stack',
    },
    startDate: start,
    endDate: end,
    theme,
  })

  return (
    <div className="grid-layout">
      <Epg isLoading={!ready} {...getEpgProps()}>
        <Layout
          {...getLayoutProps()}
          renderTimeline={(props) => <CustomTimeline {...props} isWeekView={isWeekMode} />}
          renderProgram={({ program, ...rest }) => (
            <ReservationGridItem
              key={program.data.id}
              program={program}
              onClaimFormClick={onClaimFormClick}
              onSelected={(id: string, isReservation: boolean) => {
                if (isReservation) {
                  const reservation = reservationsData!.reservations.find((r) => r.id === id)
                  if (reservation) {
                    onReservationSelected(reservation)
                  }
                } else {
                  const exception = reservationsData!.exceptions
                    .flatMap((ex) => [...ex.bookableExceptions, ...ex.claimCenterExceptions])
                    .find((ex) => ex.id === id.split(':')[0])
                  if (exception) {
                    onExceptionSelected(exception)
                  }
                }
              }}
              {...rest}
            />
          )}
          renderChannel={({ channel, ...rest }) => (
            <CustomChannelItem key={channel.uuid} channel={channel} {...rest} />
          )}
          renderLine={({ styles }: LineProps) => {
            return (
              <div
                style={{
                  ...styles.position,
                  left: styles.position.left + 10,
                  width: '95%',
                  background: '#cdcdd6',
                  pointerEvents: 'none',
                }}
              />
            )
          }}
          renderGridCell={({ id, ...rest }) => <GridCell key={id} {...rest} />}
        />
      </Epg>
    </div>
  )
}
